// screens-mewsli.tsx - Marketing: Mewsli game page
// Standard site chrome and theme throughout, except the hero header, which
// stays a fixed light scrim over the game's key art - same pattern as the
// other game pages, tuned lighter to match Mewsli's kawaii art direction
// rather than reusing the dark horror/tactical treatment.
//
// Links straight to mewsli.sstk.studio, same posture as screens-sunder.tsx/
// screens-ninelives.tsx/screens-dwindling.tsx: a single-player game with
// local-save progress and no account of its own, so a bare landing works.
// Exports: ScreenMewsli
import { Glyph, MarketingFooter, MarketingNav, PlayCta } from '../components/ui-shared';
import { ImageSlot } from '../components/ImageSlot';
import { keyArt, mediaSrc, useOriginalMedia } from '../showcase/media';
import { TrailerPlayer } from '../showcase/screens-game';

function MewsliHero() {
  const media = useOriginalMedia('mewsli');
  const art = keyArt(media);
  return (
    <header style={{ position: 'relative', height: 680, display: 'flex', flexDirection: 'column', background: '#fff1e8' }}>
      <div style={{ position: 'absolute', inset: 0 }}>
        <ImageSlot shape="rect" placeholder="Mewsli hero key art"
          src={mediaSrc(art?.url) ?? undefined}
          alt={art?.alt ?? ""}
          style={{ width: '100%', height: '100%', display: 'block' }} />
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(180deg, rgba(255,241,232,0.35) 0%, rgba(255,241,232,0.08) 40%, rgba(255,241,232,0.96) 90%)' }} />
      </div>
      <div style={{ position: 'relative', marginTop: 'auto', maxWidth: 1180, width: '100%', marginLeft: 'auto', marginRight: 'auto', padding: '0 28px 72px', display: 'flex', flexDirection: 'column', gap: 22 }}>
        <span className="ss-eyebrow" style={{ color: '#8a5a3f' }}>A Soulstack Original · Cosy Match-3</span>
        <h1 className="ss-display" style={{
          fontSize: 'clamp(3rem, 18vw, 7.25rem)', lineHeight: 0.9, letterSpacing: '0.02em', textTransform: 'uppercase', color: '#3a2a20',
          textShadow: '0 0 60px rgba(255,220,190,0.4)',
        }}>Mewsli</h1>
        <p style={{ color: 'rgba(58,42,32,0.72)', fontSize: 17.5, maxWidth: 480, lineHeight: 1.65, textWrap: 'pretty' }}>
          A kawaii cat cafe match-3, five chapters deep. Match treats on the
          grid to serve a growing roster of cafe cats.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 6, flexWrap: 'wrap' }}>
          <PlayCta className="ss-btn ss-btn--primary ss-btn--lg" href="https://mewsli.sstk.studio">Play in browser</PlayCta>
        </div>
        <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(58,42,32,0.45)' }}>
          Free to play · In the browser or installed · Mobile and desktop
        </span>
      </div>
    </header>
  );
}

export const MEWSLI_PILLARS = [
  { k: '500', title: 'Levels, five chapters', copy: 'Bakery, tea room, garden patio, coffee counter, boba stand - each hosted by its own cafe cat.' },
  { k: '100%', title: 'Original art', copy: 'Every piece, board and cat is procedurally baked in-house. No stock, no AI-generated art.' },
  { k: '0 GB', title: 'Instant to play', copy: 'The whole game streams to a browser tab and installs to a home screen, fully responsive on mobile and desktop.' },
];

function MewsliPillars() {
  const items = MEWSLI_PILLARS;
  return (
    <section className="mk-section">
      <div className="mk-inner">
        <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
          {items.map((it) => (
            <article key={it.title} className="ss-card" style={{ padding: '34px 32px 36px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <span className="ss-display" style={{ fontSize: 44, color: 'var(--ss-accent)' }}>{it.k}</span>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--ss-text)' }}>{it.title}</h3>
              <p style={{ color: 'var(--ss-text-2)', fontSize: 14.5, textWrap: 'pretty' }}>{it.copy}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function MewsliMedia() {
  const media = useOriginalMedia('mewsli');
  const shots = media?.screenshots ?? [];
  return (
    <section className="mk-section" style={{ paddingTop: 0 }}>
      <div className="mk-inner">
        <div className="mk-section-head">
          <span className="ss-eyebrow ss-eyebrow--dim">Media</span>
          <h2 className="ss-display" style={{ fontSize: 40 }}>Around the cafe</h2>
        </div>
        {media?.trailer && (
          <div style={{ marginBottom: 16 }}>
            <TrailerPlayer media={media} name="Mewsli" />
          </div>
        )}
        <div className="ss-grid-collapse mk-media-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gridTemplateRows: '300px 300px', gap: 16 }}>
          <ImageSlot shape="rounded" radius={14} placeholder="Gameplay still: wide" src={mediaSrc(shots[0]?.url) ?? undefined} alt={shots[0]?.alt ?? ""} style={{ gridRow: '1 / 3', width: '100%', height: '100%', display: 'block' }} />
          <ImageSlot shape="rounded" radius={14} placeholder="Gameplay still" src={mediaSrc(shots[1]?.url) ?? undefined} alt={shots[1]?.alt ?? ""} style={{ width: '100%', height: '100%', display: 'block' }} />
          <ImageSlot shape="rounded" radius={14} placeholder="Gameplay still" src={mediaSrc(shots[2]?.url) ?? undefined} alt={shots[2]?.alt ?? ""} style={{ width: '100%', height: '100%', display: 'block' }} />
        </div>
      </div>
    </section>
  );
}

function MewsliCta() {
  return (
    <section style={{ padding: '110px 28px', textAlign: 'center', borderTop: '1px solid var(--ss-border-soft)' }}>
      <div style={{ maxWidth: 560, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
        <h2 className="ss-display" style={{ fontSize: 48, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ss-text)' }}>Pull up a seat.</h2>
        <PlayCta className="ss-btn ss-btn--primary ss-btn--lg" href="https://mewsli.sstk.studio">Play Mewsli now</PlayCta>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, color: 'var(--ss-text-3)' }}>
          <Glyph size={16} />
          <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Built and hosted on Soulstack Studio</span>
        </div>
      </div>
    </section>
  );
}

export function ScreenMewsli() {
  return (
    <div className="ss-screen">
      <MarketingNav active="Showcase" />
      <MewsliHero />
      <MewsliPillars />
      <MewsliMedia />
      <MewsliCta />
      <MarketingFooter />
    </div>
  );
}
